/**
 * Share Service
 *
 * API client for shareable pages functionality.
 * Handles both authenticated endpoints (for share owners) and
 * public endpoints (for share viewers).
 */

import { apiGet, apiPost, apiPatch, apiDelete } from './api';

const API_BASE = '/api/v1/shares';
const PUBLIC_API_BASE = '/api/v1/public/shares';

// Get the API base URL for public (unauthenticated) requests
const getPublicApiUrl = (path) => {
    const baseUrl = process.env.REACT_APP_API_URL || '';
    return `${baseUrl}${path}`;
};

// =============================================================================
// Authenticated API (requires currentUser)
// =============================================================================

/**
 * Create a new share
 * @param {Object} shareData - Share configuration
 * @param {string} shareData.resource_type - Type of resource ('scenario', 'project', etc.)
 * @param {string} shareData.resource_id - ID of the resource to share
 * @param {string} shareData.access_level - Access level ('public', 'password', 'email_list')
 * @param {string} [shareData.password] - Password for password-protected shares
 * @param {Array<string>} [shareData.allowed_emails] - Allowed emails for email_list access
 * @param {string} [shareData.expires_at] - ISO date string for expiration
 * @param {boolean} [shareData.allow_download] - Whether to allow downloads
 * @param {Object} currentUser - Firebase user object
 * @returns {Promise<Object>} Created share with share_id and share_url
 */
export async function createShare(shareData, currentUser) {
    const response = await apiPost(API_BASE, shareData, currentUser);
    if (!response.ok) {
        let errorDetail;
        try {
            const error = await response.json();
            errorDetail = error.error || error.message || error.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Create a collection share bundling multiple existing shares
 * @param {Object} collectionData - Collection configuration
 * @param {string} collectionData.title - Display title for the collection
 * @param {Array<string>} collectionData.child_share_ids - Ordered list of share IDs to include
 * @param {Array<string>} [collectionData.child_labels] - Custom tab labels per project
 * @param {boolean} [collectionData.require_password] - Whether to require password
 * @param {string} [collectionData.password] - Password for protected collections
 * @param {number} [collectionData.expiration_days] - Days until expiration (0 = never)
 * @param {Object} currentUser - Firebase user object
 * @returns {Promise<Object>} Created collection with share_id and share_url
 */
export async function createCollection(collectionData, currentUser) {
    const response = await apiPost(`${API_BASE}/collection`, collectionData, currentUser);
    if (!response.ok) {
        let errorDetail;
        try {
            const error = await response.json();
            errorDetail = error.error || error.message || error.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
}

/**
 * List all shares for the current user
 * @param {Object} currentUser - Firebase user object
 * @param {string} [statusFilter] - Filter by status ('active', 'expired', 'revoked')
 * @returns {Promise<Object>} Object containing shares array
 */
export async function listShares(currentUser, statusFilter = null) {
    const url = statusFilter ? `${API_BASE}?status=${statusFilter}` : API_BASE;
    const response = await apiGet(url, currentUser);
    if (!response.ok) {
        let errorDetail;
        try {
            const data = await response.json();
            errorDetail = data.error || data.message || data.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Get a specific share's details
 * @param {string} shareId - Share ID
 * @param {Object} currentUser - Firebase user object
 * @returns {Promise<Object>} Share details
 */
export async function getShare(shareId, currentUser) {
    const response = await apiGet(`${API_BASE}/${shareId}`, currentUser);
    if (!response.ok) {
        let errorDetail;
        try {
            const data = await response.json();
            errorDetail = data.error || data.message || data.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Update share settings
 * @param {string} shareId - Share ID
 * @param {Object} updates - Fields to update
 * @param {string} [updates.access_level] - New access level
 * @param {string} [updates.password] - New password
 * @param {Array<string>} [updates.allowed_emails] - Updated allowed emails
 * @param {string} [updates.expires_at] - New expiration date
 * @param {boolean} [updates.allow_download] - Updated download permission
 * @param {Object} currentUser - Firebase user object
 * @returns {Promise<Object>} Updated share
 */
export async function updateShare(shareId, updates, currentUser) {
    const response = await apiPatch(`${API_BASE}/${shareId}`, updates, currentUser);
    if (!response.ok) {
        let errorDetail;
        try {
            const error = await response.json();
            errorDetail = error.error || error.message || error.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Revoke a share (soft delete)
 * @param {string} shareId - Share ID
 * @param {Object} currentUser - Firebase user object
 * @returns {Promise<Object>} Success response
 */
export async function revokeShare(shareId, currentUser) {
    const response = await apiDelete(`${API_BASE}/${shareId}`, currentUser);
    if (!response.ok) {
        let errorDetail;
        try {
            const data = await response.json();
            errorDetail = data.error || data.message || data.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Permanently delete a share
 * @param {string} shareId - Share ID
 * @param {Object} currentUser - Firebase user object
 * @returns {Promise<Object>} Success response
 */
export async function deleteShare(shareId, currentUser) {
    const response = await apiDelete(`${API_BASE}/${shareId}?permanent=true`, currentUser);
    if (!response.ok) {
        let errorDetail;
        try {
            const data = await response.json();
            errorDetail = data.error || data.message || data.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Get view history for a share
 * @param {string} shareId - Share ID
 * @param {Object} currentUser - Firebase user object
 * @returns {Promise<Object>} Object containing views array
 */
export async function getShareViews(shareId, currentUser) {
    const response = await apiGet(`${API_BASE}/${shareId}/views`, currentUser);
    if (!response.ok) {
        let errorDetail;
        try {
            const data = await response.json();
            errorDetail = data.error || data.message || data.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
}

// =============================================================================
// Public API (no auth required)
// =============================================================================

/**
 * Get share metadata (public)
 * Returns minimal info needed to display access form (requires_password, etc.)
 * @param {string} shareId - Share ID
 * @returns {Promise<Object>} Share metadata
 */
export async function getShareMeta(shareId) {
    const response = await fetch(getPublicApiUrl(`${PUBLIC_API_BASE}/${shareId}/meta`));
    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Page not found');
        }
        let errorDetail;
        try {
            const data = await response.json();
            errorDetail = data.error || data.message || data.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Validate access and get token (public)
 * @param {string} shareId - Share ID
 * @param {string} [password] - Password for password-protected shares
 * @param {string} [email] - Email for email_list access
 * @returns {Promise<Object>} Access token and share metadata
 */
export async function validateShareAccess(shareId, password = null, email = null) {
    const response = await fetch(getPublicApiUrl(`${PUBLIC_API_BASE}/${shareId}/access`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Important: include cookies
        body: JSON.stringify({ password, email }),
    });

    if (!response.ok) {
        let errorDetail;
        try {
            const error = await response.json();
            errorDetail = error.error || error.message || error.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Get share data (requires prior access validation)
 * @param {string} shareId - Share ID
 * @param {string} [accessToken] - Access token from validateShareAccess (fallback when cross-site cookies are blocked)
 * @returns {Promise<Object>} Full share data including resource data
 */
export async function getShareData(shareId, accessToken = null) {
    const headers = {};
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(getPublicApiUrl(`${PUBLIC_API_BASE}/${shareId}/data`), {
        credentials: 'include', // Include access token cookie (when browser allows)
        headers,
    });

    if (!response.ok) {
        if (response.status === 401) {
            throw new Error('Access token expired');
        }
        let errorDetail;
        try {
            const error = await response.json();
            errorDetail = error.error || error.message || error.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Record a download (public, requires access token)
 * @param {string} shareId - Share ID
 * @param {string} viewId - View ID from the current session
 * @param {string} format - Download format ('pdf', 'excel', etc.)
 * @param {string} [accessToken] - Access token fallback for cross-site cookie blocking
 * @returns {Promise<boolean>} True if recorded successfully
 */
export async function recordDownload(shareId, viewId, format, accessToken = null) {
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(getPublicApiUrl(`${PUBLIC_API_BASE}/${shareId}/download`), {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ view_id: viewId, format }),
    });

    // Don't throw on failure - download tracking is non-critical
    return response.ok;
}

// =============================================================================
// Utility functions
// =============================================================================

/**
 * Generate the share URL
 * @param {string} shareId - Share ID
 * @param {string} [shareType='scenario'] - Type of share ('scenario', 'project')
 * @returns {string} Full share URL
 */
export function getShareUrl(shareId, shareType = 'scenario') {
    // Always use production URL for share links
    const baseUrl = process.env.REACT_APP_SHARE_BASE_URL || 'https://flux.vortx.us';
    // Map type to URL path segment
    const pathSegment = shareType === 'scenario_comparison' ? 'comparison' : shareType;
    return `${baseUrl}/share/${pathSegment}/${shareId}`;
}

/**
 * Copy share URL to clipboard
 * @param {string} shareId - Share ID
 * @param {string} [shareType='scenario'] - Type of share
 * @returns {Promise<boolean>} True if copied successfully
 */
export async function copyShareUrl(shareId, shareType = 'scenario') {
    const url = getShareUrl(shareId, shareType);
    try {
        await navigator.clipboard.writeText(url);
        return true;
    } catch (err) {
        console.error('Failed to copy URL:', err);
        return false;
    }
}

export default {
    // Authenticated API
    createShare,
    createCollection,
    listShares,
    getShare,
    updateShare,
    revokeShare,
    deleteShare,
    getShareViews,
    // Public API
    getShareMeta,
    validateShareAccess,
    getShareData,
    recordDownload,
    // Utilities
    getShareUrl,
    copyShareUrl,
};
